"use client";
import { Box, Typography, Stack } from "@mui/material";
import { FitnessCenter } from "@mui/icons-material";
import React from "react";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { Units } from "../../constants";
import { convertWeight } from "../../utils/units";

interface ClientActivitySlideProps {
  data: {
    activitiesCompleted: number;
    totalKgLifted: number;
    totalCaloriesEaten: number;
  };
}

export default function ClientActivitySlide({
  data,
}: ClientActivitySlideProps) {
  const formatNumber = (num: number) => {
    return Math.round(num).toLocaleString();
  };

  const user = useCurrentUser();
  const coachUnits = (user as any)?.units || Units.METRIC;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "#0a0a0a",
        position: "relative",
        color: "white",
        p: 4,
        pt: 4,
        textAlign: "center",
        "&::before": {
          content: '""',
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "70%",
          background:
            "linear-gradient(to top, rgba(139, 92, 246, 0.2), transparent)",
          pointerEvents: "none",
        },
      }}
    >
      <Box sx={{ position: "relative", zIndex: 1, width: "100%" }}>
        <FitnessCenter
          sx={{
            fontSize: 60,
            mb: 0,
            color: "#8b5cf6",
            animation: "lift 1.5s ease-in-out infinite",
            "@keyframes lift": {
              "0%, 100%": {
                transform: "translateY(0) rotate(0deg)",
              },
              "25%": {
                transform: "translateY(-10px) rotate(-10deg)",
              },
              "75%": {
                transform: "translateY(-10px) rotate(10deg)",
              },
            },
          }}
        />
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            mb: 2,
            animation: "fadeIn 0.5s ease-out",
            "@keyframes fadeIn": {
              "0%": {
                opacity: 0,
              },
              "100%": {
                opacity: 1,
              },
            },
          }}
        >
          Your Clients in Action
        </Typography>
        <Stack
          spacing={3}
          sx={{ width: "100%", maxWidth: 450, margin: "0 auto" }}
        >
          <Box
            sx={{
              bgcolor: "rgba(139, 92, 246, 0.1)",
              border: "2px solid rgba(139, 92, 246, 0.3)",
              borderRadius: 3,
              p: 3,
              animation:
                "popIn 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55) 0.3s both",
              "@keyframes popIn": {
                "0%": {
                  transform: "scale(0)",
                  opacity: 0,
                },
                "100%": {
                  transform: "scale(1)",
                  opacity: 1,
                },
              },
            }}
          >
            <Typography
              variant="h2"
              sx={{ fontWeight: 900, mb: 1, color: "#8b5cf6" }}
            >
              {formatNumber(data.activitiesCompleted)}
            </Typography>
            <Typography variant="h6">Activities Completed</Typography>
          </Box>
          <Box
            sx={{
              bgcolor: "rgba(139, 92, 246, 0.1)",
              border: "2px solid rgba(139, 92, 246, 0.3)",
              borderRadius: 3,
              p: 3,
              animation:
                "popIn 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55) 0.5s both",
              "@keyframes popIn": {
                "0%": {
                  transform: "scale(0)",
                  opacity: 0,
                },
                "100%": {
                  transform: "scale(1)",
                  opacity: 1,
                },
              },
            }}
          >
            <Typography
              variant="h2"
              sx={{ fontWeight: 900, mb: 1, color: "#8b5cf6" }}
            >
              {formatNumber(convertWeight(data.totalKgLifted, "kg", coachUnits))} {coachUnits === Units.METRIC ? "kg" : "lbs"}
            </Typography>
            <Typography variant="h6">Total Weight Lifted</Typography>
          </Box>
          <Box
            sx={{
              bgcolor: "rgba(139, 92, 246, 0.1)",
              border: "2px solid rgba(139, 92, 246, 0.3)",
              borderRadius: 3,
              p: 3,
              animation:
                "popIn 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55) 0.7s both",
              "@keyframes popIn": {
                "0%": {
                  transform: "scale(0)",
                  opacity: 0,
                },
                "100%": {
                  transform: "scale(1)",
                  opacity: 1,
                },
              },
            }}
          >
            <Typography
              variant="h2"
              sx={{ fontWeight: 900, mb: 1, color: "#8b5cf6" }}
            >
              {formatNumber(data.totalCaloriesEaten)}
            </Typography>
            <Typography variant="h6">Total Calories Tracked</Typography>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}
